import { dashboardDomain } from "@dashboard-config"
import { createCsrfMiddleware, createMiddleware, createStart } from "@tanstack/react-start"
import { instrumentRequest, type NextResult, startHeartbeat } from "../server/request-telemetry"

type CsrfRequestContext = {
  request: Request
  handlerType?: string
}

const LOCAL_TRUSTED_ORIGINS = new Set(["http://localhost:3000", "http://127.0.0.1:3000"])

function publicStage() {
  return process.env.PUBLIC_STAGE || process.env.VITE_PUBLIC_STAGE
}

function originFromHost(host: string | null, protocol = "https") {
  if (!host) return undefined
  return `${host.startsWith("localhost") || host.startsWith("127.0.0.1") ? "http" : protocol}://${host}`
}

function requestOriginCandidates(ctx: CsrfRequestContext) {
  const requestUrl = new URL(ctx.request.url)
  const forwardedProto = ctx.request.headers.get("x-forwarded-proto") || requestUrl.protocol.replace(":", "")
  const forwardedHost = ctx.request.headers.get("x-forwarded-host")
  const host = ctx.request.headers.get("host")
  const stage = publicStage()

  return new Set(
    [
      requestUrl.origin,
      originFromHost(forwardedHost, forwardedProto),
      originFromHost(host, forwardedProto),
      `https://${dashboardDomain(stage)}`,
      "https://playbrain.com",
      stage === "dev" ? "https://dev.playbrain.com" : undefined,
      stage === "staging" ? "https://staging.playbrain.com" : undefined,
      ...LOCAL_TRUSTED_ORIGINS,
    ].filter(Boolean) as string[]
  )
}

function originFromUrl(value: string) {
  try {
    return new URL(value).origin
  } catch {
    return undefined
  }
}

function isTrustedRequestOrigin(origin: string, ctx: CsrfRequestContext) {
  return requestOriginCandidates(ctx).has(origin)
}

const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === "serverFn",
  origin: isTrustedRequestOrigin,
  referer: (referer, ctx) => {
    const refererOrigin = originFromUrl(referer)
    return refererOrigin ? isTrustedRequestOrigin(refererOrigin, ctx) : false
  },
})

// Installed at module load rather than on first request: a hang that never
// reaches the middleware would otherwise leave no trace at all.
startHeartbeat()

const requestTelemetryMiddleware = createMiddleware().server(async (ctx) => {
  return instrumentRequest(ctx.request, () => ctx.next() as Promise<NextResult>) as never
})

export const startInstance = createStart(() => ({
  // Telemetry first, so its stall watchdog covers the CSRF check too - a
  // request rejected or wedged before CSRF still gets a log line.
  requestMiddleware: [requestTelemetryMiddleware, csrfMiddleware],
}))
